import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React, { useEffect, useState } from 'react';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import ContractSelect from 'features/contracts/components/ContractSelect';
import { trackPromise } from 'react-promise-tracker';
import BasicInfoSubFormSection from 'features/requirements/sections/requirementFormTypes/basicInfoSubForm/BasicInfoSubFormSection';
import IReservedCapacityFormService from 'features/requirements/services/IReservedCapacityFormService';
import IRequirementFormService from 'features/requirements/services/IRequirementFormService';
import { ReservedCapacityFormSection_PageCard } from 'features/shared/components/loaders/promiseLoadingAreas';
import OptionType from 'features/shared/components/selectInput/OptionType';
import SelectInput from 'features/shared/components/selectInput/SelectInput';
import CapacityElectricityDTO from 'features/contracts/dtos/CapacityElectricityDTO';
import AnnualCapacity from 'features/requirements/components/reservedCapacityForm/AnnualCapacity';
import ReservedCapacityValidationSubDialog from 'features/requirements/components/reservedCapacityForm/ReservedCapacityValidationSubDialog';
import BaseRequirementFormProps from 'features/requirements/models/BaseRequirementFormProps';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';
import PostResponse from 'data/PostResponse';
import { getContractDetailRootRoute } from 'features/shared/helpers/routeHelper';
import { RequirementResultDialogParams } from 'features/requirements/models/RequirementResultDialogParams';
import InsertRequirementResultDTO from 'features/requirements/dtos/InsertRequirementResultDTO';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { appendQueryParams, getQueryParams } from 'features/shared/helpers/queryParams';
import DataQuery from 'data/DataQuery';
import { ContractType } from 'features/contracts/enums/contractType';
import { ContractStatus } from 'features/contracts/enums/contractStatus';
import { useQuery } from 'data/hooks/useQuery';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { Customer_ContractDropdown } from 'data/endpoints';
import { useSelector } from 'react-redux';
import IAppState from 'infrastructure/store/IAppState';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import { reservedCapacityElectricityInitValidations, setYearlyCapacityValidation, validateAllCapacities, validateReduceOfYearlyCapacity } from './reservedCapacityElectricitySharedValidations';
import Alert from 'features/shared/alerts/components/Alert';
import { ContractCategory } from 'features/contracts/enums/contractCategory';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import { DataLoadStatus } from 'data/enums/dataLoadStatus';
import { formatNumber } from 'features/shared/helpers/Formatters';
import ReservedCapacityInfoTooltip from 'features/contracts/components/reservedCapacity/ReservedCapacityInfoTooltip';

interface Props extends BaseRequirementFormProps {
  selectedContractId?: string;
  selectedYear?: number;
}

const ReservedCapacityElectricityYearlyFormSection = (props?: Props) => {
  //#region translations
  const tLabels = useTranslation("requirements\\requirements").t;
  const tTerms = useTranslation("shared\\terms").t; // must load here, because transaltions of terms are used in ContentDialog
  //#endregion translations

  // #region router location
  const { state: selectedContractIdLocation  } = useLocation<string>();
  // //#endregion router location

  //#region Props
  const selectedContractId = props?.selectedContractId ? props.selectedContractId : selectedContractIdLocation;
  const useOutsideOfRequirements = props?.useOutsideOfRequirements ? true : false;
  const sendButtonTitle = props?.sendButtonTitle ? props.sendButtonTitle : tLabels("reservedCapacityFormSection.sendRequirement");
  const selectedYear = props?.selectedYear !== undefined ? props.selectedYear : null; 
  //#endregion Props

  //#region DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);
  const formService = useInjection<IReservedCapacityFormService>(IocTypes.ReservedCapacityFormService);
  const baseFormService = useInjection<IRequirementFormService>(IocTypes.RequirementFormService);
  //#endregion DI

  //#region store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;
  //#endregion store

  //#region state
  const [selectedContract, setSelectedContract] = useState<ContractDropdownDTO>({});
  const [years, setYears] = useState<OptionType[]>([]);
  const [months, setMonths] = useState<OptionType[]>([]);
  const [capacities, setCapacities] = useState<CapacityElectricityDTO[]>([]);
  const [capacitiesLoadStatus, setCapacitiesLoadStatus] = useState<DataLoadStatus>("BeforeFirstLoad");
  const [hasDraftCapacity, setHasDraftCapacity] = useState<boolean>(false);
  //#endregion state

  //#region fetch data
  let contractsQueryParams = getQueryParams("ContractStatus", [ContractStatus.Effective, ContractStatus.Valid]);
  contractsQueryParams.append("ContractType", ContractType.ElectricityHighVoltage);
  appendQueryParams(contractsQueryParams, "ContractCategory", [ContractCategory.CombinedServices, ContractCategory.Distribution]);

  let contractsQuery: DataQuery = {
    url: Customer_ContractDropdown,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: contractsQueryParams
  };
  const { data: contracts } = useQuery<ContractDropdownDTO[]>(contractsQuery, [], [customer]);
  //#endregion fetch data

  //#region form hook
  const {
    values,
    setFormValue,
    errors,
    bindField,
    bindUncontrolledField,
    valideteForm,
    validateFormField,
    setValidationRule,
    setValidationError,
    clearValidationErrors
  } = useForm({
    initialValidations: reservedCapacityElectricityInitValidations(),
  });
  //#endregion

  //#region side-effects
  useEffect(() => {
    baseFormService.setDefaultContract(contracts, contractChangeHandler, selectedContractId);
  }, [selectedContractId, contracts])
  //#endregion

  //#region helpers
  const validateEntireForm = () => {
    let formValidation = valideteForm();
    let capacitiesValidation = capacities.length > 0;

    if (capacitiesValidation) {
      capacitiesValidation = validateAllCapacities(
        values,
        capacities,
        "",
        validateFormField,
        setValidationError,
        true // validate capacities and don't show error input
      );
      // setting error to yearly capacity input
      if (!capacitiesValidation) {
        let yearlyCapacityError = tLabels("reservedCapacityElectricityYearlyFormSection.yearlyCapacityError", {
          reserverPower: formatNumber(capacities[0].reserverPower!, 3)
        });
        setValidationError("DESC_TMZMSMLEEKAPROCNI", yearlyCapacityError);
      } else {
        setValidationError("DESC_TMZMSMLEEKAPROCNI", null, "remove");
      }
    }

    return formValidation && capacitiesValidation;
  }
  
  const getSelectedYear = (years: OptionType[]): number => {
    let year = years[0].value;
    
    if (selectedYear !== null) {
      let yearOption = years.find(x => x.value === `${selectedYear}`);
      year = yearOption ? yearOption.value : year;
    }

    return Number(year);
  }
  //#endregion helpers

  //#region handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateEntireForm()) {
      let isValid = await validateReduceOfYearlyCapacity(values, capacities, formService);
      if (isValid) {
        createRequirementHandler();
      } else {
        dialogService.openDialog(tLabels("reservedCapacityFormSection.warning"), () => (
          <ReservedCapacityValidationSubDialog
            createRequirementHandler={createRequirementHandler}
          />
        ));
      }
    }
  };

  const createRequirementHandler = async () => {
    let preparedValues = formService.prepareValuesToSend(values);
    let result = await createService.createRequirement("ZMENAKAPACITYELEKTRINAROCNI", preparedValues);
    openResultDialogHandler(result);
  }

  const openResultDialogHandler = (result: PostResponse<InsertRequirementResultDTO>) => {
    const params: RequirementResultDialogParams = {
      response: result,
      successMessage: tLabels("reservedCapacityFormSection.successMessage"),
      errorMessage: tLabels("reservedCapacityFormSection.errorMessage"),
      actionBtn: { title: tLabels("reservedCapacityFormSection.reservedCapacity"), route: getContractDetailRootRoute(values["DESC_TMZMSMLEESMLOUVA"], "reserved-capacity") },
      transitonBtn: null
    };

    openRequirementResultDialog(useOutsideOfRequirements, params, tLabels("reservedCapacityFormSection.changeReservedCapacity"));
  }

  const contractChangeHandler = async (contract: ContractDropdownDTO | null) => {
    if (contract) {
      let generatedYears = formService.generateYears(contract);

      // set state
      setSelectedContract(contract);
      setYears(generatedYears);

      // set select data to form values
      setFormValue("DESC_TMZMSMLEESMLOUVA", contract.contractId);

      // select year
      yearChangeHandler(getSelectedYear(generatedYears), contract, generatedYears);
    }
  }

  const yearChangeHandler = async (year: number, contract?: ContractDropdownDTO, generatedYears?: OptionType[]) => {
    let generatedMonths = formService.generateMonths(year);
    let selectedMonth = generatedMonths[0].value;

    // because state update is async (for call from contractChangeHandler)
    let selContract = contract ?? selectedContract; 
    let genYears = generatedYears ?? years;

    // fetch data
    let electricityCapacities = await trackPromise(
      formService.fetchElectricityCapacities(selContract.contractId!, year, false),
      ReservedCapacityFormSection_PageCard
    );
    let validCapacities = electricityCapacities.filter(x => x.type === "Valid");
    let hasDraftCapacity = await formService.hasDraftCapacity(selContract.contractId!, year, genYears, electricityCapacities);

    // set state
    setMonths(generatedMonths);
    setCapacities(validCapacities);
    setCapacitiesLoadStatus("AfterFirstLoad");
    setHasDraftCapacity(hasDraftCapacity);

    // set select data to form values
    setFormValue("Year", year);
    setFormValue("Month", selectedMonth);
    formService.setDefaultAnnualCapacity(validCapacities, Number(selectedMonth), setFormValue);

    // set capacity validations
    setYearlyCapacityValidation(validCapacities, setValidationRule);

    // after setting values clear errors (if switching contracts/years)
    clearValidationErrors();
  }
  
  const monthChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formService.setDefaultReservedCapacities(capacities, Number(e.target.value), setFormValue);
    setFormValue("Month", e.target.value);    
  }
  //#endregion handlers

  //#region render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard 
        title={tLabels("reservedCapacityElectricityYearlyFormSection.cardTitle")}
        bodyClassName="pt-4 px-4"
        loadingAreaName={ReservedCapacityFormSection_PageCard}
        loadingDelay={0}
      >
        <div className="row no-gutters mx-n2">
          <div className="col-lg-6 px-2">
            <ContractSelect 
              name="DESC_TMZMSMLEESMLOUVA"
              contracts={contracts}
              onChange={contractChangeHandler}
              errors={errors}
              defaultContract={selectedContract}
            />
          </div>
          <div className="col-sm-6 col-lg-3 px-2">
            <SelectInput 
              name="Year"
              options={years} 
              label={tLabels("reservedCapacityElectricityYearlyFormSection.reservedCapacityYear")}
              onChange={(e) => yearChangeHandler(Number(e.target.value))}
              value={values["Year"]}
              visible={baseFormService.isFilledValues(values, ["DESC_TMZMSMLEESMLOUVA"])}
            />
          </div>
          <div className="col-sm-6 col-lg-3 px-2">
            <SelectInput 
              name="Month"
              options={months} 
              label={tLabels("reservedCapacityElectricityYearlyFormSection.reservedCapacityMonth")}
              onChange={monthChangeHandler}
              value={values["Month"]}
              visible={baseFormService.isFilledValues(values, ["DESC_TMZMSMLEESMLOUVA"])}
            />
          </div>
          <div className="col-sm-6 px-2">
          <RenderWrapper
            isVisible={() => !hasDraftCapacity}>
            <AnnualCapacity 
              bindField={bindField}
              onBlur={() => {}}
              errors={errors}
              editable={baseFormService.isFilledValues(values, ["DESC_TMZMSMLEEPRIKONPUVOD"])}
            />
            </RenderWrapper>
          </div>
        </div>
      </PageCard>

      <NoContentWrapper
        data={capacities}
        loadStatus={capacitiesLoadStatus}
        noContentAsAlert
        noContentRender={() => (
          <Alert
            className="my-4"
            alertObj={{
              alertType: "Warning",
              message: tLabels("reservedCapacitySubForm.message"),
            }}
          />
        )}
      >
        <></>
      </NoContentWrapper>

      <RenderWrapper
        isVisible={() => !hasDraftCapacity}
        noVisibleRender={() => (
          <Alert
            className="my-4"
            alertObj={{
              alertType: "Warning",
              message: tLabels("reservedCapacityElectricityMonthlyFormSection.duplicityDraftCapacityError"),
            }}
          />
        )}
        children={<></>}
      />

      <RenderWrapper isVisible={() => !useOutsideOfRequirements}>
        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />
      </RenderWrapper>

      <div className="d-inline-flex align-items-center">
        <button
          type="submit"
          className="btn btn-primary px-3 py-2"
          disabled={hasDraftCapacity || capacities.length == 0}
        >
          {sendButtonTitle}
        </button>
        <ReservedCapacityInfoTooltip type="electricity" />
      </div>
    </form>
  )
  //#endregion render
}

export default ReservedCapacityElectricityYearlyFormSection;